import { useState, useCallback, useMemo } from 'react';
import {
  createAssignment,
  toggleAssignmentStatus,
  listEvents,
  deleteEvent,
  createEvent,
  updateEvent,
  getCurrentLocation,
  getOpenAssignments
} from '../../../services/api/eventService';
import { useSettings } from '../../../contexts/settingsContext';

/**
 * Custom hook for managing events
 * Encapsulates all event-related state and operations
 */
export function useEventManager() {
  const settings = useSettings();
  const [allEvents, setAllEvents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [nextToken, setNextToken] = useState(null);
  const [hasMore, setHasMore] = useState(true);
  const [pageSize, setPageSize] = useState(20);

  // Calculate open assignments with useMemo for better performance
  const openAssignments = useMemo(() => {
    // Filter assignments with status='open'
    return allEvents.filter(e => e.eventType === 'assignment' && e.status === 'open');
  }, [allEvents]);

  /**
   * Get location and open assignment context for event creation
   * Single source of truth for all events
   */
  const getEventContext = useCallback(async () => {
    const location = await getCurrentLocation(settings);
    const openAssignment = openAssignments.length > 0 ? openAssignments[0] : null;

    return {
      location,
      openAssignment: openAssignment ? {
        eventId: openAssignment.eventId,
        description: openAssignment.description,
        createdAt: openAssignment.createdAt,
        eventType: openAssignment.eventType
      } : null
    };
  }, [openAssignments, settings]);

  /**
   * Load initial events (first page)
   * Preserves object references for unchanged events to prevent unnecessary rerenders
   * Also fetches the user's open assignment separately to ensure it's always available
   * Note: Each user can only have ONE open assignment at a time
   */
  const loadAllEvents = useCallback(async () => {
    try {
      setLoading(true);

      // Fetch both regular events and open assignments in parallel
      const [eventsResponse, openAssignmentsData] = await Promise.all([
        listEvents({ limit: pageSize }),
        getOpenAssignments()
      ]);

      const newEvents = eventsResponse.events || [];
      const token = eventsResponse.nextToken || null;

      // Since user can only have one open assignment, take the first one (if any)
      const openAssignmentsArray = openAssignmentsData || [];
      const openAssignment = openAssignmentsArray.length > 0 ? openAssignmentsArray[0] : null;

      // Preserve existing event object references where data hasn't changed
      setAllEvents(prevEvents => {
        // Create a map of previous events by ID
        const prevEventsMap = new Map(prevEvents.map(e => [e.eventId, e]));

        // Check if open assignment is already in the regular events list
        const eventIdsFromList = new Set(newEvents.map(e => e.eventId));
        const needsOpenAssignment = openAssignment && !eventIdsFromList.has(openAssignment.eventId);

        // Merge: open assignment first (if not in list), then regular events
        const allNewEvents = needsOpenAssignment
          ? [openAssignment, ...newEvents]
          : newEvents;

        // Reuse previous event objects if data is identical
        return allNewEvents.map(newEvent => {
          const prevEvent = prevEventsMap.get(newEvent.eventId);
          if (prevEvent &&
              prevEvent.description === newEvent.description &&
              prevEvent.createdAt === newEvent.createdAt &&
              prevEvent.updatedAt === newEvent.updatedAt &&
              prevEvent.status === newEvent.status) {
            return prevEvent; // Reuse same object reference
          }
          return newEvent; // Use new object (it's new or changed)
        });
      });

      setNextToken(token);
      setHasMore(!!token);
      setError(null);
    } catch (err) {
      console.error('Failed to load events:', err);
      setError('Failed to load events');
    } finally {
      setLoading(false);
    }
  }, [pageSize]);

  /**
   * Load more events (next page) for infinite scroll
   */
  const loadMoreEvents = useCallback(async () => {
    // Use functional updates to access latest state without dependencies
    let shouldProceed = false;
    let currentToken = null;
    let currentHasMore = false;

    // Get all current values in one pass
    setLoadingMore(prev => {
      if (prev) {
        shouldProceed = false;
        return prev; // Already loading
      }
      return prev; // Don't set to true yet
    });

    setHasMore(prev => {
      currentHasMore = prev;
      return prev;
    });

    setNextToken(prev => {
      currentToken = prev;
      return prev;
    });

    // Now check if we should proceed
    if (shouldProceed === false && currentHasMore && currentToken) {
      shouldProceed = true;
    }

    if (!shouldProceed || !currentHasMore || !currentToken) {
      return;
    }

    // Now set loading to true
    setLoadingMore(true);

    try {
      const response = await listEvents({ limit: pageSize, nextToken: currentToken });
      const newEvents = response.events || [];
      const token = response.nextToken || null;

      // Append new events to existing ones
      setAllEvents(prevEvents => {
        const prevEventsMap = new Map(prevEvents.map(e => [e.eventId, e]));

        // Only add events that don't already exist
        const uniqueNewEvents = newEvents.filter(e => !prevEventsMap.has(e.eventId));

        return [...prevEvents, ...uniqueNewEvents];
      });

      setNextToken(token);
      setHasMore(!!token);
    } catch (err) {
      console.error('Failed to load more events:', err);
      setError('Failed to load more events');
    } finally {
      setLoadingMore(false);
    }
  }, [pageSize]);

  /**
   * Change page size and reload events
   */
  const changePageSize = useCallback((newSize) => {
    setPageSize(newSize);
    setAllEvents([]);
    setNextToken(null);
    setHasMore(true);
    setLoadingMore(false);
    // loadAllEvents will be called automatically via useEffect in HistoryManager
  }, []);

  /**
   * Create a new assignment (new-style with status field)
   * @param {string} description - Assignment description
   */
  const createNewAssignment = async (description) => {
    if (!description.trim()) {
      setError('Description is required');
      return false;
    }

    const now = new Date().toISOString();
    const tempId = `temp-${Date.now()}-${Math.random()}`;

    try {
      setLoading(true);

      // Get event context (location)
      const context = await getEventContext();

      // Close any currently open assignments
      const currentOpenAssignment = openAssignments.find(a => a.eventType === 'assignment' && a.status === 'open');
      if (currentOpenAssignment) {
        // Optimistically close the current assignment
        setAllEvents(prev => prev.map(e =>
          e.eventId === currentOpenAssignment.eventId
            ? { ...e, status: 'closed', updatedAt: now }
            : e
        ));

        // Close it on the server (don't wait for response)
        toggleAssignmentStatus(currentOpenAssignment.eventId, 'closed').catch(err => {
          console.error('Failed to close previous assignment:', err);
          // Revert on error
          setAllEvents(prev => prev.map(e =>
            e.eventId === currentOpenAssignment.eventId
              ? { ...e, status: 'open', updatedAt: currentOpenAssignment.updatedAt }
              : e
          ));
        });
      }

      // Optimistically add new assignment to UI
      const optimisticEvent = {
        eventId: tempId,
        eventType: 'assignment',
        description,
        status: 'open',
        createdAt: now,
        updatedAt: now,
        _optimistic: true
      };
      setAllEvents(prev => [optimisticEvent, ...prev]);

      // Create new-style assignment with status='open'
      const result = await createAssignment(description, context.location, 'open');

      // Replace optimistic event with real one
      setAllEvents(prev => prev.map(e => e.eventId === tempId ? result : e));
      setLoading(false);
      return true;
    } catch (err) {
      console.error('Failed to create assignment:', err);
      setError('Failed to create assignment');
      // Remove optimistic event on error
      setAllEvents(prev => prev.filter(e => e.eventId !== tempId));
      setLoading(false);
      return false;
    }
  };

  /**
   * Toggle assignment status between open and closed
   * @param {string} assignmentEventId - ID of the assignment to toggle
   */
  const toggleAssignment = async (assignmentEventId) => {
    if (!assignmentEventId) return false;

    try {
      setLoading(true);

      // Find the assignment
      const assignment = allEvents.find(e => e.eventId === assignmentEventId);
      if (!assignment || assignment.eventType !== 'assignment') {
        setError('Invalid assignment');
        setLoading(false);
        return false;
      }

      // Toggle status
      const newStatus = assignment.status === 'open' ? 'closed' : 'open';

      // Optimistically update UI
      setAllEvents(prev => prev.map(e =>
        e.eventId === assignmentEventId
          ? { ...e, status: newStatus, updatedAt: new Date().toISOString() }
          : e
      ));

      // Update on server
      const result = await toggleAssignmentStatus(assignmentEventId, newStatus);

      // Update with server response
      setAllEvents(prev => prev.map(e => e.eventId === assignmentEventId ? result : e));
      setLoading(false);
      return true;
    } catch (err) {
      console.error('Failed to toggle assignment:', err);
      setError('Failed to toggle assignment');
      // Reload to get correct state
      await loadAllEvents();
      setLoading(false);
      return false;
    }
  };


  /**
   * Create a new note
   * @param {string} noteText - Note text content
   */
  const createNewNote = async (noteText) => {
    if (!noteText.trim()) {
      setError('Note text is required');
      return false;
    }

    const now = new Date().toISOString();
    const tempId = `temp-${Date.now()}-${Math.random()}`;

    try {
      setLoading(true);

      // Get event context (location + open assignment)
      const context = await getEventContext();

      // Create note with new metadata.notes structure
      const newNote = {
        text: noteText,
        createdAt: now,
        ...(context.location && { location: context.location })
      };

      // Build metadata with openAssignment if present
      // Extract assignmentEventId from openAssignment for explicit parent reference
      const assignmentEventId = context.openAssignment?.eventId || null;
      const metadata = {
        notes: [newNote],
        ...(context.openAssignment && { openAssignment: context.openAssignment }),
        ...(assignmentEventId && { assignmentEventId })
      };

      // Optimistically add note to UI
      const optimisticEvent = {
        eventId: tempId,
        eventType: 'note',
        description: noteText,
        createdAt: now,
        updatedAt: now,
        metadata,
        _optimistic: true
      };
      setAllEvents(prev => [optimisticEvent, ...prev]);

      const result = await createEvent({
        eventType: 'note',
        description: noteText,
        location: context.location,
        metadata
      });

      // Replace optimistic event with real one
      setAllEvents(prev => prev.map(e =>
        e.eventId === tempId ? result : e
      ));
      setLoading(false);
      return true;
    } catch (err) {
      console.error('Failed to create note:', err);
      setError('Failed to create note');
      setAllEvents(prev => prev.filter(e => e.eventId !== tempId));
      setLoading(false);
      return false;
    }
  };

  /**
   * Update an event's description
   * @param {string} eventId - ID of the event to update
   * @param {string} newDescription - New description text
   */
  const updateEventDescription = async (eventId, newDescription) => {
    if (!newDescription.trim()) {
      setError('Description is required');
      return false;
    }

    // Store old description for rollback
    const oldEvent = allEvents.find(e => e.eventId === eventId);
    const oldDescription = oldEvent?.description;

    try {
      setLoading(true);

      // Optimistically update description
      setAllEvents(prev => prev.map(e =>
        e.eventId === eventId
          ? { ...e, description: newDescription, updatedAt: new Date().toISOString() }
          : e
      ));

      await updateEvent(eventId, { description: newDescription });
      setLoading(false);
      return true;
    } catch (err) {
      console.error('Failed to update event:', err);
      setError('Failed to update event');
      // Rollback on error
      setAllEvents(prev => prev.map(e =>
        e.eventId === eventId ? { ...e, description: oldDescription } : e
      ));
      setLoading(false);
      return false;
    }
  };

  /**
   * Delete an event (and associated events if applicable)
   * @param {Object} event - Event to delete
   */
  const deleteEventById = async (event) => {
    if (!event) return false;

    try {
      setLoading(true);

      // Optimistically remove event from UI
      setAllEvents(prev => prev.filter(e => e.eventId !== event.eventId));

      // Delete from backend
      await deleteEvent(event.eventId);

      setLoading(false);
      return true;
    } catch (err) {
      console.error('Failed to delete event:', err);
      setError('Failed to delete event');
      // Rollback on error - restore deleted event
      setAllEvents(prev => [event, ...prev].sort((a, b) =>
        new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
      ));
      setLoading(false);
      return false;
    }
  };

  /**
   * Clear error message
   */
  const clearError = () => setError(null);

  /**
   * Clear success message
   */
  const clearSuccess = () => setSuccess(null);

  return {
    // State
    allEvents,
    openAssignments,
    loading,
    loadingMore,
    error,
    success,
    hasMore,
    pageSize,

    // Actions
    loadAllEvents,
    loadMoreEvents,
    changePageSize,
    createNewAssignment,
    toggleAssignment,
    createNewNote,
    updateEventDescription,
    deleteEventById,
    clearError,
    clearSuccess
  };
}
